import { Injectable } from '@angular/core';
import { LocationService } from './location.service';
import { DepositTypeEnum, Location, LocationStatus } from '../models/location.model';
import { Expert } from '../models/expert.model';
import { Appointment, AppointmentPaymentStatus, PaymentTypeEnum } from '../models/appointment.model';
import { AppointmentItem } from '../models/appointment-item.model';
import { ServiceService } from './service.service';
import { ExpertsService } from './experts.service';
import { SubscribedComponent } from '../lib/subscribed.component';
import { environment } from '@env/environment';
import { PAYMENT_GATEWAY_PROVIDER_CONFIGURATIONS } from '../models/payment-gateway.model';

@Injectable({ providedIn: 'root' })
export class AppointmentService extends SubscribedComponent {
  appointmentPaymentStatus = AppointmentPaymentStatus;
  defaultLocationUuid = environment.defaultLocationUuid;
  private location?: Location;

  constructor(
    private locationService: LocationService,
    private expertsService: ExpertsService,
    private serviceService: ServiceService,
  ) {
    super();
    this.location = this.locationService.getLocation();
    this.locationService.locationChanged.pipe().subscribe((location) => {
      this.location = location;
    });
  }

  getStandaloneItems(appointment: Appointment): AppointmentItem[] {
    if (!appointment?.appointmentItems) return [];
    return appointment.appointmentItems.filter((item) => item.parentItemId === null);
  }

  // Items
  getItem(appointment: Appointment, serviceItemId: number): AppointmentItem | void {
    for (const item of appointment.appointmentItems) {
      if (item.serviceItemId === serviceItemId) {
        return item;
      }
    }
  }

  getAddonsForItem(appointment: Appointment, itemId: number): AppointmentItem[] {
    return appointment.appointmentItems.filter((item) => item.parentItemId === itemId);
  }

  getAddonsServiceItemIds(appointment: Appointment, itemId: number): number[] {
    return this.getAddonsForItem(appointment, itemId).map((item) => item.serviceItemId);
  }

  getTotalPriceForItem(appointment: Appointment, item: AppointmentItem): number {
    let price = item.choicePrice;

    const addons = this.getAddonsForItem(appointment, item.id);
    for (const addon of addons) {
      price += addon.choicePrice;
    }
    return price;
  }

  getTotalDurationForItem(appointment: Appointment, item: AppointmentItem): number {
    let duration = item.choiceDuration;
    const addons = this.getAddonsForItem(appointment, item.id);
    for (const addon of addons) {
      duration += addon.choiceDuration;
    }
    return duration;
  }

  isEmpty(appointment: Appointment): boolean {
    return appointment.appointmentItems && appointment.appointmentItems.length === 0;
  }

  isCheckoutDisabled(appointment: Appointment, selfCheckout = false): boolean {
    const checkoutEnabled =
      appointment?.test ||
      this.location?.uuid === this.defaultLocationUuid ||
      this.location?.demo ||
      (this.location?.status === LocationStatus.ACTIVE &&
        (this.location?.onlineOrderingEnabled || this.isStaffInitiated(appointment, selfCheckout)));
    return !checkoutEnabled;
  }

  // experts
  getExpertsForAppointment(appointment: Appointment): Expert[] {
    let experts: Expert[] = [];

    for (const item of appointment.appointmentItems) {
      experts.push(...this.getExpertsForAppointmentItem(appointment, item.serviceItemId, true));
    }

    // remove duplicates
    const uniqueExpertIds = Array.from(new Set(experts.map((expert) => expert.id)));
    experts = uniqueExpertIds.map((id) => experts.find((expert) => expert.id === id));

    return experts;
  }

  getExpertsForAppointmentItem(appointment: Appointment, serviceItemId: number, returnAll: boolean): Expert[] {
    const item = appointment.appointmentItems.find((item) => item.serviceItemId === serviceItemId);
    const expertId = item.expertId;

    if (returnAll || !expertId) {
      const experts = this.serviceService.getExpertsForService(this.serviceService.getService(serviceItemId), false);
      return experts;
    } else {
      return [this.expertsService.getExpertDetails(expertId)];
    }
  }

  isCardOnFileFlow(
    paymentType: PaymentTypeEnum | string | null,
    appointment: Appointment | null,
    location: Location | null,
  ): boolean {
    return (
      paymentType === PaymentTypeEnum.Venue &&
      location?.cardOnFileDepositType !== DepositTypeEnum.None &&
      this.hasCardOnFileGateway(location) &&
      !this.isOnlineOrderingDisabled(appointment, location)
    );
  }

  /** Card-on-file only works through a gateway that can save a card (Stripe) - otherwise "pay at venue" needs no payment. */
  hasCardOnFileGateway(location: Location | null): boolean {
    return (location?.paymentGateways ?? []).some(
      (gateway) => PAYMENT_GATEWAY_PROVIDER_CONFIGURATIONS[gateway.provider]?.cardOnFile,
    );
  }

  getRemainingAmount(appointment: Appointment | null): number {
    return Math.max((appointment?.totalPrice ?? 0) - (appointment?.prePaidAmount ?? 0), 0);
  }

  isOnlineOrderingDisabled(appointment: Appointment | null, location: Location | null, selfCheckout = false): boolean {
    return location?.onlineOrderingEnabled === false && !this.isStaffInitiated(appointment, selfCheckout);
  }

  /** Flows started by staff don't depend on online ordering. */
  private isStaffInitiated(appointment: Appointment | null, selfCheckout: boolean): boolean {
    return selfCheckout || appointment?.utmSource === 'in-salon';
  }
}
